import type { VisibleSidebarSection } from '@portal/platform/configuration';

/** The page is `route` or under it. */
export function isActive(pathname: string, route: string | undefined): boolean {
  if (!route) {
    return false;
  }
  const base = route.replace(/\/+$/, '');
  return pathname === base || pathname.startsWith(`${base}/`);
}

export interface Crumb {
  label: string;
  route?: string;
}

interface ActiveMatch {
  sectionId: string;
  trail: Crumb[];
}

/**
 * The menu entry the page belongs to: the one whose route matches it most closely, with its
 * section and, for a child entry, its parent.
 */
function activeMatch(sections: VisibleSidebarSection[], pathname: string): ActiveMatch | null {
  let best: (ActiveMatch & { length: number }) | null = null;
  for (const section of sections) {
    // A headerless group (the external links) is not a place anyone navigates "through".
    const sectionCrumbs: Crumb[] =
      section.label && !section.flat ? [{ label: section.label, route: section.route }] : [];
    const consider = (route: string | undefined, trail: Crumb[]) => {
      if (route && isActive(pathname, route) && (!best || route.length > best.length)) {
        best = { sectionId: section.id, trail: [...sectionCrumbs, ...trail], length: route.length };
      }
    };
    // The section's own overview page is the section itself: its one crumb.
    consider(section.route, []);
    for (const item of section.items) {
      if (item.externalUrl) {
        continue;
      }
      const parent: Crumb = { label: item.label, route: item.route };
      consider(item.route, [parent]);
      for (const child of item.children ?? []) {
        consider(child.route, [parent, { label: child.label, route: child.route }]);
      }
    }
  }
  const match = best as (ActiveMatch & { length: number }) | null;
  return match ? { sectionId: match.sectionId, trail: match.trail } : null;
}

/**
 * The way to the page: its section (linked to its overview when it has one), the entry, and the
 * child entry when it is one. Empty when no entry matches (e.g. the profile page).
 */
export function breadcrumbTrail(sections: VisibleSidebarSection[], pathname: string): Crumb[] {
  return activeMatch(sections, pathname)?.trail ?? [];
}

/** The id of the section holding the page's entry, or null. */
export function activeSectionId(
  sections: VisibleSidebarSection[],
  pathname: string,
): string | null {
  return activeMatch(sections, pathname)?.sectionId ?? null;
}
